import React, { useEffect, useState } from "react";

import { isPortalLicenseLocked, clearPortalLicenseLock } from "../api/portalClient";

const RECOVERY_POLL_MS = 30_000;

/**
 * Full-screen "temporarily unavailable" overlay for the customer portal when
 * the appliance license is hard-locked (expired past grace, revoked, or trial
 * ended). The portal's axios 402 interceptor latches the lock and dispatches
 * `portal-license:locked`; we listen and replace the portal with a calm notice
 * instead of pages full of 402 errors / endless spinners.
 *
 * Unlike the tech-facing LicenseGate, this is deliberately non-technical: the
 * customer can't renew the MSP's license, so the copy points them at their
 * provider and never mentions "license". We seed state from the latched flag so
 * a freshly-mounted portal page (after navigation) shows the screen at once.
 */
export default function PortalLicenseGate({ children }: { children: React.ReactNode }) {
  const [locked, setLocked] = useState(isPortalLicenseLocked);

  useEffect(() => {
    const onLocked = () => setLocked(true);
    window.addEventListener("portal-license:locked", onLocked);
    return () => window.removeEventListener("portal-license:locked", onLocked);
  }, []);

  // Auto-recover: while locked, poll the (license-gated) /me endpoint until the
  // appliance stops returning 402, then reload so the app re-bootstraps cleanly
  // (the auth context's /me load 402'd while locked, so a plain re-render would
  // leave us logged out). We hit it with a bare fetch rather than portalApi so
  // the response interceptor's 401-redirect / 402-relatch side effects don't
  // fire — here we only care whether the status is still 402. A 200 (valid
  // session) or 401 (license back, token stale → login) both mean recovered.
  useEffect(() => {
    if (!locked) return;
    let cancelled = false;
    const id = window.setInterval(async () => {
      try {
        const token = localStorage.getItem("portal_token");
        const res = await fetch("/api/v1/portal/me", {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        if (!cancelled && res.status !== 402) {
          clearPortalLicenseLock();
          window.location.reload();
        }
      } catch {
        // Network blip — keep waiting; the next tick retries.
      }
    }, RECOVERY_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [locked]);

  if (!locked) return <>{children}</>;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-lg border border-gray-100 p-8 text-center">
        <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
          <svg
            className="w-6 h-6 text-amber-600"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 8v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
            />
          </svg>
        </div>
        <h1 className="text-xl font-semibold text-gray-900">Portal temporarily unavailable</h1>
        <p className="mt-2 text-sm text-gray-600">
          This service is paused while your IT provider updates their account. Please check back
          shortly, or contact your provider if you need immediate assistance.
        </p>
      </div>
    </div>
  );
}
